// Implements: F-UIX-009
/**
 * Revoking a session, which is two different operations wearing one button.
 *
 * Revoking somebody else's device refreshes the list: `DELETE` answers `204`
 * and `["auth","sessions"]` is invalidated, so the row disappears because the
 * server said so and not because the client removed it optimistically.
 *
 * Revoking the row whose `current` is `true` is a SIGN-OUT. The cookie this
 * browser is holding stops being valid at that exact moment, so refreshing the
 * list would ask with a dead session and land on a `401`. The capability says
 * it plainly, and so does this hook: same cache clear, same `/login`.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { AUTH_SESSIONS_KEY } from "../../../shared/api/queryKeys";
import type { SessionSummary } from "../../../types/api";
import { revokeSession } from "../api/authApi";
import { endSession } from "./useLogout";

export interface UseRevokeSessionResult {
  /**
   * Revoke one row. It never rejects: it answers `true` when the session is
   * gone and `false` when it is not, and the failure is also left on `failure`.
   *
   * The answer exists because `failure` cannot be read by the caller that did
   * the awaiting — that closure was built on the previous render and still
   * holds the previous value — and a caller that cannot tell the two apart ends
   * up announcing a revocation the server refused.
   */
  readonly revoke: (session: SessionSummary) => Promise<boolean>;
  /** A revocation is on its way. */
  readonly pending: boolean;
  /** The failure of the last attempt, or `null` when it worked. */
  readonly failure: Error | null;
}

export function useRevokeSession(): UseRevokeSessionResult {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<Error | null>(null);
  const inFlight = useRef(false);

  const revoke = useCallback(
    async (session: SessionSummary): Promise<boolean> => {
      // A second press while the first request is open revoked nothing, so it
      // answers `false`: only the attempt that reached the server may claim it.
      if (inFlight.current) return false;
      inFlight.current = true;
      setPending(true);
      setFailure(null);
      try {
        await revokeSession(session.id);
        if (session.current) {
          endSession(queryClient, navigate);
          return true;
        }
        await queryClient.invalidateQueries({ queryKey: AUTH_SESSIONS_KEY });
        return true;
      } catch (cause) {
        setFailure(cause instanceof Error ? cause : new Error(String(cause)));
        return false;
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [queryClient, navigate],
  );

  return { revoke, pending, failure };
}
